import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Layers, Folder, Send } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { listBuilderItems, deleteBuilderItem, batchSubmitBuilderItems } from '@/services/api/template-builder';
import { getEsteiraFormOptions, getEsteiraGroup } from '@/services/api/esteira';
import type { BuilderItem } from '@/types/template-builder';
import PageSelectDialog from './components/PageSelectDialog';

function dedupePages(g: any): any[] {
  const seen = new Set<string>();
  const out: any[] = [];
  for (const p of g?.pages_meta ?? []) {
    const fb = p.fb_page_id ?? '';
    if (!fb || p.is_usable === false || seen.has(fb)) continue;
    seen.add(fb);
    out.push(p);
  }
  return out;
}

type StatusKey = 'draft' | 'pending' | 'approved' | 'confirmed' | 'rejected';

function statusKey(it: BuilderItem): StatusKey {
  if (it.approval_status === 'REJECTED' || it.state === 'rejected') return 'rejected';
  if (it.state === 'confirmed' || it.confirmed_at) return 'confirmed';
  if (it.approval_status === 'APPROVED' || it.state === 'approved' || it.state === 'tested') return 'approved';
  if (it.approval_status === 'PENDING' || it.state === 'submitted') return 'pending';
  return 'draft';
}
const STATUS_LABEL: Record<StatusKey, string> = { draft: 'Rascunho', pending: 'Em análise', approved: 'Aprovado', confirmed: 'Confirmado', rejected: 'Reprovado' };
const STATUS_CLASS: Record<StatusKey, string> = {
  draft: 'bg-slate-100 text-slate-600',
  pending: 'bg-indigo-50 text-indigo-600',
  approved: 'bg-amber-50 text-amber-700',
  confirmed: 'bg-emerald-600 text-white',
  rejected: 'bg-red-50 text-red-600',
};
const STRIPES = 'repeating-linear-gradient(45deg, #eef1f5, #eef1f5 10px, #e7ebf1 10px, #e7ebf1 20px)';

function ago(s?: string | null): string {
  if (!s) return '';
  const d = new Date(String(s).replace(' ', 'T'));
  const sec = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (sec < 3600) return `há ${Math.floor(sec / 60)} min`;
  if (sec < 86400) return `há ${Math.floor(sec / 3600)} h`;
  return `há ${Math.floor(sec / 86400)} dias`;
}

const TABS: Array<{ key: 'all' | StatusKey; label: string }> = [
  { key: 'all', label: 'Todas' }, { key: 'draft', label: 'Rascunho' },
  { key: 'pending', label: 'Em análise' }, { key: 'approved', label: 'Aprovado' },
  { key: 'confirmed', label: 'Confirmado' }, { key: 'rejected', label: 'Reprovado' },
];

export default function TemplateBuilderPage() {
  const navigate = useNavigate();
  const [projectFilter, setProjectFilter] = useState<number | 'all'>('all');
  const [tab, setTab] = useState<'all' | StatusKey>('all');
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleGroup = (k: string) => setExpanded((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const GROUP_LIMIT = 3;

  const { data: formOpts } = useQuery({ queryKey: ['template-builder', 'projects'], queryFn: getEsteiraFormOptions });
  const projects = formOpts?.projects ?? [];

  const { data, isLoading } = useQuery({
    queryKey: ['template-builder', 'items', projectFilter],
    queryFn: () => listBuilderItems(projectFilter === 'all' ? undefined : projectFilter),
    refetchOnMount: 'always',        // ao voltar do wizard, recarrega sempre
    refetchOnWindowFocus: true,
  });
  const items = data?.items ?? [];

  const queryClient = useQueryClient();
  const del = useMutation({
    mutationFn: (id: number) => deleteBuilderItem(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['template-builder', 'items'] }),
  });
  const onDelete = (e: React.MouseEvent, it: BuilderItem) => {
    e.stopPropagation();
    if (del.isPending) return;
    if (window.confirm(`Excluir a peça "${it.name ?? `peca-${it.id}`}"? Isso remove o template e as aprovações. Não dá pra desfazer.`)) {
      del.mutate(it.id);
    }
  };

  const { toast } = useToast();
  // submeter rascunhos em lote: escolhe 1 página da esteira → submete todos
  const [submitTarget, setSubmitTarget] = useState<{ esteiraGroupId: number; esteiraName?: string | null; draftIds: number[] } | null>(null);
  const { data: submitEsteira } = useQuery({
    queryKey: ['template-builder', 'submit-esteira', submitTarget?.esteiraGroupId],
    queryFn: () => getEsteiraGroup(submitTarget!.esteiraGroupId),
    enabled: !!submitTarget?.esteiraGroupId,
  });
  const submitPages = useMemo(() => dedupePages(submitEsteira), [submitEsteira]);
  const batchMut = useMutation({
    mutationFn: ({ ids, pageId }: { ids: number[]; pageId: string }) => batchSubmitBuilderItems(ids, pageId),
    onSuccess: (r) => {
      toast({ title: `${r.submitted}/${r.total} enviados à Meta`, description: r.submitted < r.total ? 'Alguns falharam — abra a peça pra ver o motivo.' : 'Acompanhe o status na listagem.' });
      queryClient.invalidateQueries({ queryKey: ['template-builder', 'items'] });
      setSubmitTarget(null);
    },
    onError: (e: any) => toast({ title: 'Falha no envio em lote', description: String(e?.message ?? e), variant: 'destructive' }),
  });

  const stats = useMemo(() => {
    const s = { total: items.length, approved: 0, confirmed: 0, draft: 0, rejected: 0 };
    for (const it of items) { const k = statusKey(it); if (k === 'approved') s.approved++; else if (k === 'confirmed') s.confirmed++; else if (k === 'draft') s.draft++; else if (k === 'rejected') s.rejected++; }
    return s;
  }, [items]);

  const visible = useMemo(() => {
    const query = q.trim().toLowerCase();
    return items.filter((it) => {
      if (tab !== 'all' && statusKey(it) !== tab) return false;
      if (query && !`${it.name ?? ''} ${it.title ?? ''} ${it.text_content ?? ''}`.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [items, tab, q]);

  const keyOf = (it: BuilderItem) => `${it.esteira_group_id}::${it.folder_id ?? 'root'}`;
  // contagem total (não filtrada) por estrutura, pra estampar no cabeçalho do grupo
  const totalsByKey = useMemo(() => {
    const m = new Map<string, { total: number; confirmed: number }>();
    for (const it of items) { const t = m.get(keyOf(it)) ?? { total: 0, confirmed: 0 }; t.total++; if (statusKey(it) === 'confirmed') t.confirmed++; m.set(keyOf(it), t); }
    return m;
  }, [items]);
  // agrupa as peças VISÍVEIS (respeita filtro/busca) por esteira+pasta
  const groups = useMemo(() => {
    const m = new Map<string, { key: string; esteiraGroupId: number; esteiraName?: string | null; folderId: number | null; folderName?: string | null; projectId: number | null; items: BuilderItem[] }>();
    for (const it of visible) {
      const key = keyOf(it);
      if (!m.has(key)) m.set(key, { key, esteiraGroupId: it.esteira_group_id, esteiraName: it.esteira_name, folderId: it.folder_id ?? null, folderName: it.folder_name, projectId: it.project_id ?? null, items: [] });
      m.get(key)!.items.push(it);
    }
    return [...m.values()].sort((a, b) => (a.esteiraName ?? '').localeCompare(b.esteiraName ?? '') || (a.folderName ?? '').localeCompare(b.folderName ?? ''));
  }, [visible]);

  const goNewInStructure = (esteiraGroupId: number, folderId: number | null, projectId: number | null) =>
    navigate('/meta/template-builder/new', { state: { esteiraGroupId, folderId, projectId } });

  const renderCard = (it: BuilderItem) => {
    const k = statusKey(it);
    return (
      <div key={it.id} role="button" tabIndex={0} onClick={() => navigate(`/meta/template-builder/${it.id}`)} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/meta/template-builder/${it.id}`); }} className="group relative cursor-pointer overflow-hidden rounded-2xl border bg-white text-left transition hover:shadow-md">
        <div className="relative h-40 w-full" style={it.image_url ? undefined : { background: STRIPES }}>
          {it.image_url && <img src={it.image_url} alt="" className="h-full w-full object-cover" />}
          <span className={`absolute right-3 top-3 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[k]}`}>{STATUS_LABEL[k]}</span>
          <button type="button" onClick={(e) => onDelete(e, it)} disabled={del.isPending} title="Excluir peça" className="absolute left-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-slate-500 opacity-0 shadow-sm transition hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 disabled:opacity-50">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-1.5 p-4">
          <div className="font-mono text-xs text-muted-foreground">{it.name ?? `peca-${it.id}`}</div>
          <div className="text-lg font-semibold">{it.title || 'Sem título'}</div>
          <p className="line-clamp-2 text-sm text-muted-foreground">{it.text_content?.trim() || 'sem texto'}</p>
          <div className="flex items-center justify-between border-t pt-2 text-xs text-muted-foreground">
            <span className="truncate">{it.folder_name || 'Raiz'}</span>
            <span className="shrink-0">{ago(it.created_at)}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Suas peças</h1>
          <p className="text-sm text-muted-foreground">Cria um template, aprova numa página da esteira, testa num lead e confirma.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input placeholder="Buscar peça…" value={q} onChange={(e) => setQ(e.target.value)} className="w-52" />
          <select className="h-9 rounded-md border bg-background px-2 text-sm" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
            <option value="all">Todos os projetos</option>
            {projects.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
          </select>
          <Button onClick={() => navigate('/meta/template-builder/new')}><Plus className="mr-1 h-4 w-4" /> Nova peça</Button>
        </div>
      </div>

      {/* stat cards */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
        {[
          { label: 'TOTAL', v: stats.total, c: 'text-slate-900' },
          { label: 'CONFIRMADAS', v: stats.confirmed, c: 'text-emerald-600' },
          { label: 'APROVADAS', v: stats.approved, c: 'text-amber-600' },
          { label: 'RASCUNHOS', v: stats.draft, c: 'text-slate-900' },
          { label: 'REPROVADAS', v: stats.rejected, c: 'text-red-600' },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border bg-white p-4">
            <div className="text-xs font-medium tracking-wide text-muted-foreground">{s.label}</div>
            <div className={`text-2xl font-bold ${s.c}`}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* tabs */}
      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${tab === t.key ? 'bg-indigo-600 text-white' : 'border bg-white text-slate-600 hover:bg-muted'}`}>{t.label}</button>
        ))}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-64 animate-pulse rounded-2xl border bg-slate-100" />)}
        </div>
      ) : groups.length === 0 ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <button onClick={() => navigate('/meta/template-builder/new')} className="flex min-h-[16rem] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed text-muted-foreground transition hover:border-indigo-300 hover:text-indigo-600">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-indigo-50 text-indigo-500"><Plus className="h-5 w-5" /></div>
            <div className="font-medium">Nova peça</div>
            <div className="text-xs">Esteira → pasta → montar</div>
          </button>
        </div>
      ) : (
        <div className="space-y-7">
          {groups.map((grp) => {
            const totals = totalsByKey.get(grp.key) ?? { total: grp.items.length, confirmed: 0 };
            const isExpanded = expanded.has(grp.key);
            const shown = isExpanded ? grp.items : grp.items.slice(0, GROUP_LIMIT);
            const hiddenCount = grp.items.length - shown.length;
            const draftIds = grp.items.filter((it) => statusKey(it) === 'draft').map((it) => it.id);
            return (
              <section key={grp.key}>
                {/* cabeçalho do grupo: esteira · pasta + contagem + criar mais aqui */}
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3 border-b pb-2">
                  <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="flex items-center gap-1.5 font-semibold text-slate-900"><Layers className="h-4 w-4 shrink-0 text-indigo-500" /> {grp.esteiraName || `esteira #${grp.esteiraGroupId}`}</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="flex items-center gap-1.5 text-slate-700"><Folder className="h-4 w-4 shrink-0 text-slate-400" /> {grp.folderName || 'Raiz'}</span>
                    <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{totals.confirmed} confirmada{totals.confirmed === 1 ? '' : 's'} · {totals.total} no total</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {grp.items.length > GROUP_LIMIT && (
                      <button onClick={() => toggleGroup(grp.key)} className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
                        {isExpanded ? 'Mostrar menos' : `Ver todas (${grp.items.length})`}
                      </button>
                    )}
                    {draftIds.length > 0 && (
                      <Button size="sm" onClick={() => setSubmitTarget({ esteiraGroupId: grp.esteiraGroupId, esteiraName: grp.esteiraName, draftIds })} disabled={batchMut.isPending} className="bg-indigo-600 hover:bg-indigo-700">
                        <Send className="mr-1 h-4 w-4" /> Submeter rascunhos ({draftIds.length})
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => goNewInStructure(grp.esteiraGroupId, grp.folderId, grp.projectId)}>
                      <Plus className="mr-1 h-4 w-4" /> Nova peça aqui
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {shown.map(renderCard)}
                  {hiddenCount > 0 ? (
                    <button onClick={() => toggleGroup(grp.key)} className="flex min-h-[16rem] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed text-muted-foreground transition hover:border-indigo-300 hover:text-indigo-600">
                      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-indigo-50 text-lg font-bold text-indigo-500">+{hiddenCount}</div>
                      <div className="font-medium">Ver todas</div>
                      <div className="text-xs">{grp.items.length} peças nessa estrutura</div>
                    </button>
                  ) : (
                    <button onClick={() => goNewInStructure(grp.esteiraGroupId, grp.folderId, grp.projectId)} className="flex min-h-[16rem] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed text-muted-foreground transition hover:border-indigo-300 hover:text-indigo-600">
                      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-indigo-50 text-indigo-500"><Plus className="h-5 w-5" /></div>
                      <div className="font-medium">Nova peça aqui</div>
                      <div className="text-xs">mesma esteira + pasta</div>
                    </button>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <PageSelectDialog
        open={!!submitTarget}
        onOpenChange={(o) => { if (!o) setSubmitTarget(null); }}
        esteiraName={submitTarget?.esteiraName ?? undefined}
        pages={submitPages}
        onSelect={(fb) => { if (submitTarget && fb) batchMut.mutate({ ids: submitTarget.draftIds, pageId: fb }); }}
      />
    </div>
  );
}
